import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

// Import UI components - but we'll modify their usage
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Loader, Lock, Trash, User } from "lucide-react";
import { ApiService } from "@/services/api-service";
import useAuthStore from "@/store/useAuthStore";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Alert, AlertDescription } from "@/components/ui/alert";

// Schema definitions
const profileFormSchema = z.object({
  name: z.string().min(2, {
    message: "Name must be at least 2 characters.",
  }),
  email: z.string().email({
    message: "Please enter a valid email address.",
  }),
});

const passwordFormSchema = z
  .object({
    currentPassword: z.string().min(8, {
      message: "Password must be at least 8 characters.",
    }),
    newPassword: z.string().min(8, {
      message: "Password must be at least 8 characters.",
    }),
    confirmPassword: z.string().min(8, {
      message: "Password must be at least 8 characters.",
    }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ProfileFormValues = z.infer<typeof profileFormSchema>;
type PasswordFormValues = z.infer<typeof passwordFormSchema>;

export const Route = createFileRoute("/app/_layout/settings")({
  component: AccountSettings,
});

function AccountSettings() {
  const [activeTab, setActiveTab] = useState("profile");
  const queryClient = useQueryClient();
  const { updateUser } = useAuthStore();

  const { data: userData, isLoading } = useQuery({
    queryKey: ["user"],
    queryFn: () => ApiService.getUser(),
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      return ApiService.updateProfile({ ...values });
    },

    onSuccess: (data) => {
      updateUser({ ...data });
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });

  const updatePasswordMutation = useMutation({
    mutationFn: async (values: PasswordFormValues) => {
      return ApiService.updateProfile({
        previousPassword: values.currentPassword,
        password: values.newPassword,
      });
    },
    onSuccess: () => {
      passwordForm.reset({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    },
  });

  // Profile form
  const profileForm = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: "",
      email: "",
    },
    values: userData
      ? {
          name: userData.name,
          email: userData.email || "",
        }
      : undefined,
  });

  const passwordForm = useForm<PasswordFormValues>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  function onProfileSubmit(data: ProfileFormValues) {
    updateProfileMutation.mutate(data);
  }

  function onPasswordSubmit(data: PasswordFormValues) {
    updatePasswordMutation.mutate(data);
  }

  const { logout } = useAuthStore();
  const navigate = useNavigate();

  const deleteAccountMutation = useMutation({
    mutationFn: ApiService.deleteAccount,
    onSuccess() {
      logout();
      navigate({ to: "/auth/signup", search: { validate: undefined } });
    },
    onError(err) {
      console.error("Error deleting account: ", err);
      toast.error("an error occured deleting account");
    },
  });

  async function handleAccountDelete() {
    const initialConfirm = confirm(
      "Are you sure you want to delete your account? This action cannot be undone."
    );

    if (!initialConfirm) return;

    const verificationText = "DELETE";
    const secondConfirm = prompt(
      `This will permanently delete all your data and cannot be reversed. Please type "${verificationText}" to confirm.`
    );

    if (secondConfirm !== verificationText) {
      alert("Account deletion cancelled. Your account is safe.");
      return;
    }

    try {
      await deleteAccountMutation.mutateAsync();
      alert(
        "Your account has been successfully deleted. We're sorry to see you go."
      );
    } catch (error) {
      console.error("Account deletion failed:", error);
      alert(
        "There was a problem deleting your account. Please try again or contact support."
      );
    }
  }

  if (isLoading) {
    return (
      <div className="w-full max-w-4xl mx-auto">
        <div className="bg-gradient-to-br from-neutral-50 to-neutral-100 dark:from-neutral-900 dark:to-neutral-800 rounded-2xl p-6 shadow-lg">
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 rounded-full bg-gradient-to-r from-secondary-400 to-purple-500 animate-pulse"></div>
              <div className="space-y-2 flex-1">
                <div className="h-4 w-1/3 rounded bg-gradient-to-r from-neutral-300 to-neutral-200 dark:from-neutral-700 dark:to-neutral-600 animate-pulse"></div>
                <div className="h-3 w-1/4 rounded bg-gradient-to-r from-neutral-300 to-neutral-200 dark:from-neutral-700 dark:to-neutral-600 animate-pulse"></div>
              </div>
            </div>
            <div className="h-12 rounded-lg bg-gradient-to-r from-neutral-300 to-neutral-200 dark:from-neutral-700 dark:to-neutral-600 animate-pulse"></div>
            <div className="h-12 rounded-lg bg-gradient-to-r from-neutral-300 to-neutral-200 dark:from-neutral-700 dark:to-neutral-600 animate-pulse"></div>
            <div className="h-10 w-1/4 ml-auto rounded-lg bg-gradient-to-r from-secondary-400 to-purple-500 animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="mb-4">
        <h1 className="text-base font-bold">Account Settings</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mt-2 font-satoshi">
          Manage your profile information and security preferences
        </p>
      </div>

      {/* Custom styled tabs */}
      <div className="flex mb-8 bg-neutral-100 dark:bg-neutral-800 rounded-xl p-1">
        <button
          onClick={() => setActiveTab("profile")}
          className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-300 ${
            activeTab === "profile"
              ? "bg-white dark:bg-neutral-700 text-secondary-600 dark:text-secondary-400 shadow-sm"
              : "text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <User className="w-4 h-4" />
            <span className="pt-1">Profile</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab("password")}
          className={`flex-1 py-3 px-4 rounded-lg font-medium text-sm transition-all duration-300 ${
            activeTab === "password"
              ? "bg-white dark:bg-neutral-700 text-secondary-600 dark:text-secondary-400 shadow-sm"
              : "text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-white"
          }`}
        >
          <div className="flex items-center justify-center space-x-2">
            <Lock className="w-4 h-4" />
            <span className="pt-1">Password</span>
          </div>
        </button>
      </div>

      {/* Profile Tab */}
      <div
        className={`transition-all p-6 transform duration-500 h-full shadow-2xl rounded-2xl ${
          activeTab === "profile"
            ? "opacity-100 translate-x-0"
            : "opacity-0 translate-x-8 absolute"
        }`}
        style={{ display: activeTab === "profile" ? "block" : "none" }}
      >
        <div className="">
          <div className="flex items-center space-x-4 mb-8">
            <div>
              <h2 className="text-sm font-medium">Profile Information</h2>
              <p className="text-neutral-500 dark:text-neutral-400 font-satoshi ">
                Update your account profile information and email address
              </p>
            </div>
          </div>
          {updateProfileMutation.isError && (
            <Alert
              className={
                "mb-4 bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300"
              }
            >
              <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span>
                  {(updateProfileMutation.error as any)?.response?.data
                    ?.error ??
                    (updateProfileMutation.error as any)?.response?.data
                      ?.message ??
                    "something went wrong"}
                </span>
              </AlertDescription>
            </Alert>
          )}

          {updateProfileMutation.isSuccess && (
            <Alert
              className={
                "mb-4 bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300"
              }
            >
              <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span>Account information updated</span>
              </AlertDescription>
            </Alert>
          )}
          <Form {...profileForm}>
            <form
              onSubmit={profileForm.handleSubmit(onProfileSubmit)}
              className="space-y-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={profileForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-700 dark:text-neutral-300 font-medium">
                        Name
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder="Your name"
                            className=" rounded-lg text-xs font-satoshi border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 focus:ring-secondary-500 focus:border-secondary-500"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-500 text-xs" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={profileForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-700 dark:text-neutral-300 font-medium">
                        Email
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            placeholder=""
                            className="font-satoshi rounded-lg text-xs border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 focus:ring-secondary-500 focus:border-secondary-500"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-500 text-sm" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="pt-4">
                <Button
                  type="submit"
                  disabled={
                    updateProfileMutation.isPending ||
                    !profileForm.formState.isDirty
                  }
                  className="bg-secondary-600 text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-[1.02] focus:ring-4 focus:ring-secondary-300 dark:focus:ring-secondary-800 shadow-md hover:shadow-lg float-right text-xs"
                >
                  {updateProfileMutation.isPending ? (
                    <div className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Saving...
                    </div>
                  ) : (
                    "Save changes"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </div>
        <Button
          variant={"destructive"}
          className="text-xs"
          onClick={handleAccountDelete}
          disabled={deleteAccountMutation.isPending}
        >
          {deleteAccountMutation.isPending ? (
            <Loader className="w-4 h-4 animate-spin" />
          ) : (
            <Trash className="w-4 h-4" />
          )}
          Delete Account
        </Button>
      </div>

      {/* Password Tab */}
      <div
        className={`transition-all transform duration-500 ${
          activeTab === "password"
            ? "opacity-100 translate-x-0"
            : "opacity-0 -translate-x-8 absolute"
        }`}
        style={{ display: activeTab === "password" ? "block" : "none" }}
      >
        <div className="bg-white dark:bg-neutral-800 rounded-2xl p-6 shadow-lg border border-neutral-100 dark:border-neutral-700">
          <div className="flex items-center space-x-4 mb-8">
            <div>
              <h2 className="text-sm font-normal">Change Password</h2>
              <p className="text-neutral-500 dark:text-neutral-400 font-satoshi">
                Update your password to keep your account secure
              </p>
            </div>
          </div>

          {updatePasswordMutation.isError && (
            <Alert
              className={
                "mb-4 bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300"
              }
            >
              <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span>
                  {(updatePasswordMutation.error as any)?.response?.data
                    ?.error ??
                    (updatePasswordMutation.error as any)?.response?.data
                      ?.message ??
                    "something went wrong"}
                </span>
              </AlertDescription>
            </Alert>
          )}

          {updatePasswordMutation.isSuccess && (
            <Alert
              className={
                "mb-4 bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-300"
              }
            >
              <AlertDescription className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                <span>Password updated</span>
              </AlertDescription>
            </Alert>
          )}

          <Form {...passwordForm}>
            <form
              onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
              className="space-y-6"
            >
              <FormField
                control={passwordForm.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-neutral-700 dark:text-neutral-300 font-medium">
                      Current Password
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type="password"
                          placeholder="••••••••"
                          className=" rounded-lg border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 focus:ring-yellow-500 focus:border-yellow-500"
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage className="text-red-500 text-sm" />
                  </FormItem>
                )}
              />

              <div className="relative my-8">
                <Separator className="my-8" />
                <div className="absolute left-1/2 whitespace-nowrap top-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white dark:bg-neutral-800 px-4 text-neutral-400">
                  New Password
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={passwordForm.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-700 dark:text-neutral-300 font-medium">
                        New Password
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type="password"
                            placeholder="••••••••"
                            className="rounded-lg border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 focus:ring-yellow-500 focus:border-yellow-500"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormDescription className="text-neutral-500 dark:text-neutral-400 text-xs mt-1">
                        Password must be at least 8 characters long.
                      </FormDescription>
                      <FormMessage className="text-red-500 text-sm" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={passwordForm.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-neutral-700 dark:text-neutral-300 font-medium">
                        Confirm Password
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type="password"
                            placeholder="••••••••"
                            className="rounded-lg border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-900 focus:ring-yellow-500 focus:border-yellow-500"
                            {...field}
                          />
                        </div>
                      </FormControl>
                      <FormDescription className="text-neutral-500 dark:text-neutral-400 text-sm opacity-0">
                        Confirm
                      </FormDescription>
                      <FormMessage className="text-red-500 text-sm" />
                    </FormItem>
                  )}
                />
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={
                    updatePasswordMutation.isPending ||
                    !passwordForm.formState.isDirty
                  }
                  className="bg-secondary-700 text-white rounded-lg font-medium transition-all duration-200 transform hover:scale-[1.02] focus:ring-4 focus:ring-yellow-300 dark:focus:ring-yellow-800 shadow-md hover:shadow-lg text-xs"
                >
                  {updatePasswordMutation.isPending ? (
                    <div className="flex items-center">
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Updating...
                    </div>
                  ) : (
                    "Update password"
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  );
}
